import NumberFormComponent from "components/shared/FormComponent/NumberFormComponent";
import TextFormComponent from "components/shared/FormComponent/TextFormComponent";
import SwitchFormComponent from "components/shared/FormComponent/SwitchFormComponent";
import { isValidBIC, isValidIBAN } from "ibantools";
import { useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { BankingFieldKey, CountryFeatures } from "types/countryFeatures";
import { Collapse } from "@mui/material";

export type BankingMode = "local" | "international";

interface IBankingFieldsSection {
    bankingFields: BankingFieldKey[];
    validationRules: CountryFeatures["validation"];
    disabled: boolean;
    bankingMode: BankingMode;
}

const getBankingFieldPlaceholder = (
    fieldKey: string,
    bankingFieldRules: CountryFeatures["validation"]["bankingFields"],
): string => {
    const rules = bankingFieldRules?.[fieldKey as keyof typeof bankingFieldRules];
    if (!rules) return "";
    const fixedLen = "length" in rules ? rules.length : null;
    const maxLen = "maxLength" in rules ? rules.maxLength : null;
    return "0".repeat(fixedLen ?? maxLen ?? 0);
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const IntlFields = ({ disabled }: { disabled: boolean }) => {
    const [t] = useTranslation();
    const {
        control,
        formState: { errors },
    } = useFormContext();
    return (
        <div className="grid grid-cols-2 gap-2 mb-2">
            <TextFormComponent
                label="BIC / SWIFT"
                control={control}
                controlName="bankingMetadata.intl_swift"
                placeholder="CHASUS33"
                required
                customRequiredMessage={t("IbanModal.RequiredField")}
                error={(errors?.bankingMetadata as any)?.intl_swift}
                disabled={disabled}
            />
            <TextFormComponent
                label={t("IbanModal.IntlAccountNumber")}
                control={control}
                controlName="bankingMetadata.intl_account_number"
                placeholder="123456789"
                required
                customRequiredMessage={t("IbanModal.RequiredField")}
                error={(errors?.bankingMetadata as any)?.intl_account_number}
                disabled={disabled}
            />
            <TextFormComponent
                label={t("IbanModal.IntlRoutingCode")}
                control={control}
                controlName="bankingMetadata.intl_routing_code"
                placeholder="021000021"
                error={(errors?.bankingMetadata as any)?.intl_routing_code}
                disabled={disabled}
            />
            <TextFormComponent
                label={t("IbanModal.IntlBankName")}
                control={control}
                controlName="bankingMetadata.intl_bank_name"
                placeholder="JPMorgan Chase"
                error={(errors?.bankingMetadata as any)?.intl_bank_name}
                disabled={disabled}
            />
        </div>
    );
};

const IbanLocalFields = ({ disabled }: { disabled: boolean }) => {
    const [t] = useTranslation();
    const {
        control,
        watch,
        clearErrors,
        formState: { errors },
    } = useFormContext();
    const [showRibError, setShowRibError] = useState(false);

    const iban = watch("iban")?.replace(/ /g, "");
    const bic = watch("swift")?.replace(/ /g, "");
    const isForeignIban = useMemo(() => iban && iban.length > 2 && !iban.toUpperCase().startsWith("FR"), [iban]);
    const bicRequired = useMemo(() => {
        if (iban?.slice(0, 2).toUpperCase() !== "FR") return true;
        clearErrors("swift");
        return false;
    }, [iban]);

    useEffect(() => {
        if (iban?.length && bic?.length) {
            setShowRibError(!isValidIBAN(iban) || !isValidBIC(bic));
        } else if (iban?.length && !bic?.length) {
            setShowRibError(!isValidIBAN(iban));
        }
    }, [iban, bic]);

    return (
        <>
            <div className="form-component-wrapper rib gap-2 mb-2">
                <TextFormComponent
                    label={t("NewBeneficiary.Iban")}
                    control={control}
                    placeholder="FR76 0000 0000 0000 0000 0000 000"
                    error={errors?.iban as any}
                    required
                    controlName="iban"
                    disabled={disabled}
                />
                <TextFormComponent
                    label={t("NewBeneficiary.Bic")}
                    control={control}
                    error={errors?.swift as any}
                    placeholder="SOGEFRPP"
                    controlName="swift"
                    required={bicRequired}
                    disabled={disabled}
                />
            </div>
            <Collapse in={isForeignIban || false} timeout="auto" unmountOnExit>
                <div className="mb-2">
                    <NumberFormComponent
                        label={t("NewBeneficiary.RoutingNumber")}
                        control={control}
                        controlName="routingNumber"
                        error={undefined}
                    />
                </div>
            </Collapse>
            {showRibError && (
                <div className="indication-invalid-rib">
                    <span className="rib-error-txt">{t("IbanModal.IbanError")}</span>
                    <SwitchFormComponent
                        label={t("NewBeneficiary.BypassIBANValidation")}
                        labelColor="black"
                        control={control}
                        controlName="bypassIbanValidation"
                        checkbox
                    />
                </div>
            )}
        </>
    );
};

const MetadataLocalFields = ({
    fields,
    validationRules,
    disabled,
}: {
    fields: BankingFieldKey[];
    validationRules: CountryFeatures["validation"];
    disabled: boolean;
}) => {
    const [t] = useTranslation();
    const {
        control,
        formState: { errors },
    } = useFormContext();
    return (
        <div className="form-component-wrapper equal gap-2 mb-2">
            {fields.map((fieldKey) => (
                <TextFormComponent
                    key={fieldKey}
                    label={t(`Beneficiary.${fieldKey}`)}
                    control={control}
                    controlName={`bankingMetadata.${fieldKey}`}
                    placeholder={getBankingFieldPlaceholder(fieldKey, validationRules.bankingFields)}
                    disabled={disabled}
                    error={(errors?.bankingMetadata as any)?.[fieldKey]}
                />
            ))}
        </div>
    );
};

// ─── Main ─────────────────────────────────────────────────────────────────────

const BankingFieldsSection = ({ bankingFields, validationRules, disabled, bankingMode }: IBankingFieldsSection) => {
    const usesIban = bankingFields.includes("iban");
    const metadataFields = bankingFields.filter((f) => f !== "iban" && f !== "bic");

    if (bankingMode === "international") return <IntlFields disabled={disabled} />;
    if (usesIban) return <IbanLocalFields disabled={disabled} />;
    if (metadataFields.length > 0)
        return <MetadataLocalFields fields={metadataFields} validationRules={validationRules} disabled={disabled} />;
    return null;
};

export default BankingFieldsSection;
